import { Text, Drawer, useMediaQuery, Carousel, Icon } from '@lounge-fe/ui-kit'
import { UpsellsProps } from './types'
import { Item } from './Item'
import { Card } from './Card'
import useEmblaCarousel from 'embla-carousel-react'
import { useState } from 'react'
import classNames from 'classnames'
import { useCarouselContext } from '@lounge-fe/ui-kit/src/components/Carousel/Carousel'

const Arrows = () => {
  const { canScrollPrev, canScrollNext, embla } = useCarouselContext()
  return (
    <div className="flex items-center gap-4">
      <button onClick={() => embla?.scrollPrev()}>
        <Icon.ArrowLeft
          className={classNames(
            'w-4 h-4',
            canScrollPrev
              ? 'cursor-pointer'
              : 'cursor-not-allowed text-[#B0B0B0]',
          )}
        />
      </button>
      <button onClick={() => embla?.scrollNext()}>
        <Icon.ArrowRight
          className={classNames(
            'w-4 h-4',
            canScrollNext
              ? 'cursor-pointer'
              : 'cursor-not-allowed text-[#B0B0B0]',
          )}
        />
      </button>
    </div>
  )
}

export const UpsellsDrawer = ({ upsells, title, content }: UpsellsProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const isActive = upsells.length > 2

  const [emblaRef, embla] = useEmblaCarousel({
    loop: false,
    align: 'start',
    containScroll: 'trimSnaps',
    skipSnaps: true,
    active: isActive,
  })

  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.sm})`)
  const isMobile = useMediaQuery(({ screens }) => `(max-width: ${screens.md})`)

  return (
    <div
      data-testid="upsells"
      className="gap-4 justify-center sm:grid-cols-[1fr_auto] items-center sm:text-left sm:gap-6 my-6"
    >
      <Drawer>
        {({ onOpen, onClose }) => (
          <>
            <Carousel emblaRef={emblaRef} embla={embla}>
              <div className="flex justify-between items-center mb-4">
                <div className="flex flex-col gap-1 w-full text-left">
                  {title && <Text variant="title-six">{title}</Text>}
                  {isDesktop && content && (
                    <Text variant="body-md" className="text-text-subtle">
                      {content}
                    </Text>
                  )}
                </div>

                {isActive && (
                  <div className="flex items-center gap-3">
                    <Arrows />
                  </div>
                )}
              </div>
              <Carousel.Viewport>
                <Carousel.Container className="grid auto-cols-[calc(50%-4px)] gap-2">
                  {upsells.map((upsell, idx) => (
                    <Carousel.Slide key={upsell.handle} index={idx}>
                      <Card
                        onClick={() => {
                          setActiveIndex(idx)
                          onOpen()
                        }}
                        {...upsell}
                      />
                    </Carousel.Slide>
                  ))}
                </Carousel.Container>
              </Carousel.Viewport>
            </Carousel>

            <Drawer.Content direction="right" className="max-w-[450px]">
              <Drawer.Header className="sm:px-10 sm:py-8">
                {title && <Text variant="title-five">{title}</Text>}
              </Drawer.Header>
              <Drawer.Body className="sm:px-10">
                <div className="flex flex-col gap-6">
                  {activeIndex !== null && (
                    <Item
                      onSuccess={isMobile ? onClose : undefined}
                      {...upsells[activeIndex]}
                    />
                  )}
                </div>
              </Drawer.Body>
            </Drawer.Content>
          </>
        )}
      </Drawer>
    </div>
  )
}
